import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Cookies from "js-cookie";
import { formatBuildingLabel } from "../utils/apartmentDisplay";
import "../styles/modal.css";

const ContractViewModal = ({ isOpen, onClose, onConfirm, userData = {} }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasStartedScrolling, setHasStartedScrolling] = useState(false);
  const contentRef = useRef(null);

  // 기본값 설정
  const {
    block = "000",
    unit = "0000",
    name = "홍길동",
    apartment = "테스트아파트",
  } = userData;
  const assigneeOrgLabel =
    Cookies.get("apartmentCode") === "ForeonStation5"
      ? "관리단"
      : "입주자대표회의";

  useEffect(() => {
    // 모달이 열릴 때마다 스크롤 상태 초기화
    if (isOpen) {
      setHasScrolledToBottom(false);
      setHasStartedScrolling(false);

      // 모달이 열릴 때 스크롤을 맨 위로 이동
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }

      // 스크롤 가능한지 확인
      setTimeout(() => {
        const element = contentRef.current;
        if (element) {
          if (element.scrollHeight <= element.clientHeight) {
            // 스크롤이 필요 없는 경우 자동으로 스크롤 완료 처리
            setHasScrolledToBottom(true);
          }
        }
      }, 50);
    }
  }, [isOpen]);

  const handleScroll = (e) => {
    const element = e.target;

    // 스크롤이 조금이라도 시작되었는지 확인
    if (element.scrollTop > 10 && !hasStartedScrolling) {
      setHasStartedScrolling(true);
    }

    // 스크롤이 바닥에 도달했는지 확인 (여유 20px 추가)
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 20) {
      setHasScrolledToBottom(true);
    }
  };

  // 스크롤을 아래로 자동으로 내리는 함수
  const scrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: "smooth",
      });
      setHasStartedScrolling(true);
    }
  };

  // 확인 버튼 클릭 핸들러
  const handleConfirm = () => {
    if (hasScrolledToBottom) {
      onConfirm();
      onClose();
    } else {
      // 스크롤을 자동으로 내려줌
      scrollToBottom();
    }
  };

  // 애니메이션 설정
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.15,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.15,
        ease: "easeIn",
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, scale: 0.98 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1], // Custom easing for smooth motion
      },
    },
    exit: {
      opacity: 0,
      scale: 0.98,
      transition: {
        duration: 0.15,
        ease: "easeIn",
      },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="agreement-modal-overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            className="agreement-modal-content"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              maxHeight: "100vh", // 전체 화면 높이로 변경
              height: "100%", // 전체 높이 설정
              borderTopLeftRadius: "0", // 상단 모서리 각도 제거
              borderTopRightRadius: "0", // 상단 모서리 각도 제거
              boxShadow: "0 0 25px rgba(0,0,0,0.12)",
              willChange: "transform, opacity", // 성능 최적화
              transformOrigin: "center center",
            }}
          >
            <div className="agreement-modal-header">
              <div className="close-button" onClick={onClose}>
                ×
              </div>
              <h1>채권양도양수증서</h1>
            </div>

            <div
              className="agreement-modal-body"
              ref={contentRef}
              onScroll={handleScroll}
            >
              <div className="agreement-document">
                <h2 className="agreement-title">
                  채권(하자보수에 갈음하는 손해배상
                  <br />
                  청구권) 양도양수증서
                </h2>
                <p className="agreement-parties">
                  {apartment}{" "}
                  {formatBuildingLabel(block, Cookies.get("apartmentCode"))}{" "}
                  {unit}호 {name}
                  <br />
                  (이하, "갑" 이라 한다)와
                  <br />
                  {apartment} {assigneeOrgLabel} (이하, "을" 이라 한다)는
                  <br />
                  다음과 같이 계약한다.
                </p>

                <h2 className="agreement-title">변호사 선임약정서</h2>

                <div className="agreement-text">
                  <div style={{ marginBottom: "25px" }}>
                    <h4>1. 채권양도 동의</h4>
                    <p>
                      "갑"은 하자보수의무자(시행사, 시공사, 보증사 등
                      사업주체)에 대하여 가지고 있는
                      하자보수청구권(하자보수종결합의 포함) 및 하자보수에
                      갈음하는 손해배상청구권(사용검사 전, 사용검사 후 일체의
                      하자담보청구권 포함)을 "을"에게 양도하기로 하고 이에 본
                      양도증서에 서명날인한다.
                    </p>
                  </div>

                  <div style={{ marginBottom: "25px" }}>
                    <h4>2. 권한 위임</h4>
                    <p>
                      "갑"은 위 하자보수의무자들을 상대로 한 위 손해배상청구권의
                      양도통지 및 손해배상청구권행사 등 하자소송에 관한 일체의
                      권한(단, 소제기권 합의종결에 관한 권한은 제외)을 "을"에게
                      위임한다.
                    </p>
                  </div>

                  <div style={{ marginBottom: "25px" }}>
                    <h4>3. 승소금 사용방법</h4>
                    <p>
                      승소판결금(합의금, 소송비용의 환수금 포함)의 사용방법은 ①
                      계약에 의한 변호사 성공보수 및 소송대납비용은 승소금에서
                      공제하며, ② "갑"앞의 비용을 제한 나머지 금액 중 전유부분
                      판결금은 각 세대에 지급하고, 공용부분 판결금은
                      {assigneeOrgLabel}의 의결에 따라 하자보수를 위한 공사비용으로
                      사용함에 "갑"은 동의한다.
                    </p>
                  </div>

                  <div style={{ marginBottom: "25px" }}>
                    <h4>4. 동의 철회 불가</h4>
                    <p>
                      "갑"은 본 동의가 정상적인 의사와 권한으로 성립되었음을
                      확인하며 동의의사를 철회하지 않기로 하고, "을"이
                      하자보수청구 및 손해배상청구(하자소송)를 진행함에 있어
                      개인정보수집·이용·제공에 "갑"은 동의한다.
                    </p>
                  </div>

                  <div style={{ marginTop: "30px" }}>
                    <p>※ 첨부 : 신분증 사본 1부</p>
                    <p style={{ marginTop: "20px" }}>
                      {new Date().getFullYear()}년 {new Date().getMonth() + 1}월{" "}
                      {new Date().getDate()}일
                    </p>
                  </div>

                  <div className="signature-area" style={{ marginTop: "20px" }}>
                    <div>
                      <p>소유자</p>
                      <p>성 명: {name}</p>
                    </div>
                  </div>

                  <div style={{ marginTop: "30px" }}>
                    <p>
                      양수인(을) {apartment} {assigneeOrgLabel}
                    </p>
                    <p>대표자 회장 (인)</p>
                  </div>
                </div>
              </div>
            </div>

            {!hasScrolledToBottom && (
              <div className="scroll-indicator-bottom" onClick={scrollToBottom}>
                ↓ 내용을 끝까지 읽어주세요 ↓
              </div>
            )}

            <div className="agreement-modal-footer no-border">
              <button
                className="agreement-button"
                onClick={handleConfirm}
                disabled={!hasScrolledToBottom}
                style={{
                  backgroundColor: hasScrolledToBottom ? "#7A4FFF" : "#E1DCFF",
                  color: hasScrolledToBottom ? "white" : "#fff",
                  cursor: hasScrolledToBottom ? "pointer" : "not-allowed",
                }}
              >
                {hasScrolledToBottom
                  ? "내용을 확인했습니다"
                  : "내용을 모두 읽어주세요"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContractViewModal;
